import { ColumnState } from "@/components/table/DataTable";

function getNestedValue(obj: unknown, path: string): unknown {
  return path.split(".").reduce((acc: any, p) => acc?.[p], obj);
}

function sanitizeCell(value: unknown): string {
  const str = String(value ?? "").replace(/"/g, '""');
  return `"${str}"`;
}

const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/** Sem acento, sem símbolo e sem espaço — "Criação & Design" vira `criacao-design`. */
const slug = (texto: string) =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    // Nome de área é livre: sem teto, um centro mal cadastrado vira um nome de arquivo ilegível.
    .slice(0, 40)
    .replace(/-$/, "");

/**
 * O nome do arquivo baixado: o que ele é, seguido de **quando foi baixado**.
 *
 * A competência vem antes para a pasta de Downloads ordenar por período sozinha, e o `_baixado-`
 * separa as duas datas — sem essa palavra, `fechamento-2026-08-2026-08-09` são dois carimbos
 * colados que ninguém distingue. Até o minuto: baixar duas vezes no mesmo dia é o caso comum
 * (conferiu, corrigiu, baixou de novo), e é justamente aí que um nome repetido confunde.
 *
 * O relógio é o **do navegador**, de propósito: "quando eu baixei" é a hora de quem clicou, não a
 * do servidor, que roda em UTC.
 */
export function nomeArquivoExcel(...partes: string[]) {
  const agora = new Date();
  const dois = (n: number) => String(n).padStart(2, "0");
  const carimbo = `${agora.getFullYear()}-${dois(agora.getMonth() + 1)}-${dois(agora.getDate())}-${dois(agora.getHours())}${dois(agora.getMinutes())}`;

  return `${partes.filter(Boolean).map(slug).join("-")}_baixado-${carimbo}`;
}

/**
 * Baixa o .xlsx que o servidor gerou. O Excel do módulo de custo é feito no backend (o `exceljs`
 * já mora lá), então o front só dá nome ao arquivo e dispara o download — o que aqui seria uma
 * biblioteca de 1 MB no bundle.
 */
export function baixarPlanilhaExcel(dados: ArrayBuffer, filename: string) {
  const url = URL.createObjectURL(new Blob([dados], { type: XLSX_MIME }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function exportToCSV<T>(data: T[], columns: ColumnState<T>[], filename: string) {
  const exportCols = columns.filter(
    (c) => c.isVisible && c.type !== "headless" && c.key !== "select",
  );

  const headers = exportCols.map((c) => sanitizeCell(c.title));

  const rows = data.map((item) =>
    exportCols.map((c) => {
      const raw = c.raw ? c.raw(item) : getNestedValue(item, c.key);
      return sanitizeCell(raw);
    }),
  );

  const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");

  // BOM para Excel reconhecer UTF-8
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
